// ANETT HORVATH — House Runtime State (Extended)
// Global house phase + mode + preview controller.

export type HousePhase = 'closed' | 'preparation' | 'allocation_open' | 'production' | 'archive';
export type HouseMode = 'OPEN' | 'WAITLIST_ONLY' | 'MEMBERS_ONLY' | 'INNER_CIRCLE_ONLY' | 'CLOSED';

const HOUSE_STATE_KEY = 'ah_house_runtime_state';

export interface HouseRuntimeState {
  id: string;
  house_state: HousePhase;
  mode: HouseMode;
  preview_mode: boolean;
  allocation_limit: number;
  updated_at: string;
  updated_by: string;
}

const DEFAULT_STATE: HouseRuntimeState = {
  id: 'default',
  house_state: 'preparation',
  mode: 'OPEN',
  preview_mode: false,
  allocation_limit: 100,
  updated_at: new Date().toISOString(),
  updated_by: 'system',
};

function loadState(): HouseRuntimeState {
  try {
    const raw = localStorage.getItem(HOUSE_STATE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...DEFAULT_STATE, ...parsed };
    }
  } catch { /* fallback */ }
  return DEFAULT_STATE;
}

export function getHouseState(): HousePhase { return loadState().house_state; }
export function getHouseMode(): HouseMode { return loadState().mode; }
export function getPreviewMode(): boolean { return loadState().preview_mode; }
export function getHouseRuntimeRecord(): HouseRuntimeState { return loadState(); }

export function setHouseState(state: HousePhase, adminId: string): HouseRuntimeState {
  const current = loadState();
  const record: HouseRuntimeState = { ...current, id: crypto.randomUUID(), house_state: state, updated_at: new Date().toISOString(), updated_by: adminId };
  localStorage.setItem(HOUSE_STATE_KEY, JSON.stringify(record));
  return record;
}

export function setHouseMode(mode: HouseMode, adminId: string): HouseRuntimeState {
  const current = loadState();
  const record: HouseRuntimeState = { ...current, mode, updated_at: new Date().toISOString(), updated_by: adminId };
  localStorage.setItem(HOUSE_STATE_KEY, JSON.stringify(record));
  return record;
}

export function setPreviewMode(preview: boolean, adminId: string): HouseRuntimeState {
  const current = loadState();
  const record: HouseRuntimeState = { ...current, preview_mode: preview, updated_at: new Date().toISOString(), updated_by: adminId };
  localStorage.setItem(HOUSE_STATE_KEY, JSON.stringify(record));
  return record;
}

export function setAllocationLimit(limit: number, adminId: string): HouseRuntimeState {
  const current = loadState();
  const record: HouseRuntimeState = { ...current, allocation_limit: limit, updated_at: new Date().toISOString(), updated_by: adminId };
  localStorage.setItem(HOUSE_STATE_KEY, JSON.stringify(record));
  return record;
}

export function updateFullHouseState(updates: Partial<HouseRuntimeState>, adminId: string): HouseRuntimeState {
  const current = loadState();
  const record: HouseRuntimeState = { ...current, ...updates, updated_at: new Date().toISOString(), updated_by: adminId };
  localStorage.setItem(HOUSE_STATE_KEY, JSON.stringify(record));
  return record;
}

export const HOUSE_PHASES: HousePhase[] = ['closed', 'preparation', 'allocation_open', 'production', 'archive'];
export const HOUSE_MODES: HouseMode[] = ['OPEN', 'WAITLIST_ONLY', 'MEMBERS_ONLY', 'INNER_CIRCLE_ONLY', 'CLOSED'];
